import type { AxiosError, AxiosInstance } from 'axios';

import type { ApiErrorType, ServerError } from 'models';
import { useIdentityFlowDialogStore } from 'stores/zustand/identityFlowDialogStore';

// Endpoints that are expected to return this error:
// GET /users/:user_id/plaid/accounts/:bank_id/immediate_funding
// GET /users/:user_id/action_verification_mfa (only when actionType = withdrawal)
// POST /users/:user_id/stripe/ani_deposit
// POST /users/:user_id/stripe/cashapp_payment_intent
// POST /users/:user_id/stripe/withdrawal
// POST /users/:user_id/stripe/payment_intent
// POST /users/:user_id/debit/payment_session
// POST /users/:user_id/debit/payment
// POST /users/:user_id/debit/withdrawal
// POST /users/:user_id/deposits
// POST /users/:user_id/withdrawals
// POST /users/:user_id/plaid/deposit
// POST /users/:user_id/plaid/withdrawal
// POST /users/:user_id/mesh/initiate_mesh_withdrawal
// POST /users/:user_id/braintree/deposit
// POST /users/:user_id/braintree/withdrawal
// POST /users/:user_id/crypto/withdrawal

export const STEP_UP_ERROR_CODE = 'step_up_verification_required';

export function isStepUpServerError(error: ServerError): boolean {
    return error.conflict && error.error?.code === STEP_UP_ERROR_CODE;
}

function getErrorCode(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    if (typeof d.code === 'string') return d.code;
    const nested = d.error;
    if (nested && typeof nested === 'object' && typeof (nested as Record<string, unknown>).code === 'string') {
        return (nested as Record<string, unknown>).code as string;
    }
    return undefined;
}

function isStepUpError(error: AxiosError<ApiErrorType>): boolean {
    return error.response?.status === 409 && getErrorCode(error.response?.data) === STEP_UP_ERROR_CODE;
}

export function attachStepUpInterceptor(client: AxiosInstance): void {
    client.interceptors.response.use(undefined, (error: AxiosError<ApiErrorType>) => {
        if (isStepUpError(error)) {
            useIdentityFlowDialogStore.getState().actions.opened();
        }
        return Promise.reject(error);
    });
}

// The below code is for testing purposes only, to mock the step-up verification flow without needing to trigger it from the server.
// It will cause all requests to the specified endpoints to return a step-up verification error, which can be used to test the frontend handling of this flow.
// This should only be used in a local development environment and not in production.
const STEP_UP_ENDPOINTS = [
    /\/plaid\/accounts\/[^/]+\/immediate_funding/,
    /\/action_verification_mfa/,
    /\/stripe\/ani_deposit$/,
    /\/stripe\/cashapp_payment_intent$/,
    /\/stripe\/withdrawal$/,
    /\/stripe\/payment_intent$/,
    /\/debit\/payment_session$/,
    /\/debit\/payment$/,
    /\/debit\/withdrawal$/,
    // /\/deposits$/,
    // /\/withdrawals$/,
    /\/plaid\/deposit$/,
    /\/plaid\/withdrawal$/,
    /\/mesh\/initiate_mesh_withdrawal$/,
    /\/braintree\/deposit$/,
    /\/braintree\/withdrawal$/,
    /\/crypto\/withdrawal$/,
];

const STEP_UP_MOCK_DATA: ApiErrorType = {
    error: {
        code: STEP_UP_ERROR_CODE,
        message: '[mock] step-up verification required',
        details: '',
        service: '',
        data: {},
    },
};

export function attachStepUpMockInterceptor(client: AxiosInstance): void {
    client.interceptors.response.use((response) => {
        const url = response.config.url ?? '';
        if (!STEP_UP_ENDPOINTS.some((pattern) => pattern.test(url))) return response;

        const error: AxiosError<ApiErrorType> = {
            isAxiosError: true,
            name: 'AxiosError',
            message: '[mock] step_up_verification_required',
            config: response.config,
            request: response.request,
            response: { ...response, status: 409, data: STEP_UP_MOCK_DATA },
            toJSON: () => ({}),
        };
        return Promise.reject(error);
    });
}
